import { cacheExchange } from '@urql/exchange-graphcache';
import { relayPagination } from '@urql/exchange-graphcache/extras';
import { gql } from 'urql';
// Import ScheduleDocument from generated file for proper AST structure
// (The ~/graphql/queries/Schedule.ts version uses graphql() which may not match)
import { SCHEDULE_DATE_RANGE_FILTER } from './config/scheduleFeatureFlags';
import {
  GraphCacheConfig,
  JobStatus,
  JobsDocument,
  PlaceTypeOrderMode,
  QueryJobBoardArgs,
  // types
  type JobsQuery,
  type MutationRenamePlaceTypeArgs,
  type MutationUpdatePlaceTypeConfigArgs,
  type PlaceTypeConfig,
  type WithTypename,
} from './generated/graphql';
import schema from './generated/schema';
import {
  ContactsDocument,
  ItemsDocument,
  ListJobsDocument,
  ScheduleDocument,
  ScheduleQuery,
  // types
  type ContactsQuery,
  type ItemsQuery,
  type ListJobsQuery,
  type ScheduleEventFullFragment,
  type ScheduleEventStartEndFragment,
  type ScheduleEventStatusFragment,
  type ScheduleEventTimesFragment,
  type UpdateAttributeValuesMutation,
} from './gql/graphql';
import {
  getScheduleEventCompositeKey,
  isBookingEvent,
} from './helpers/scheduleEventKey';
import { getRegisteredScheduleVariables } from './helpers/scheduleQueryVariables';
import { jobTimesCacheFields } from './helpers/jobTimesCacheFields';
import { validateAndNormalizeTimezone } from './helpers/timezoneUtils';

// Reusable GraphQL fragments and queries
// Use generated document for PlaceTypeConfig to avoid duplicate operation definitions
import { PlaceTypeConfigDocument } from './generated/graphql';
// Inline document for ContactTypeConfig to mirror update behaviour for contacts
const ContactTypeConfigDocument = gql`
  query ContactTypeConfig_Cache {
    contactTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`;

// ScheduleEvent fragments
const ScheduleEventStartEnd = gql`
  fragment ScheduleEventStartEnd on ScheduleEvent {
    id
    start
    end
    timeZone
  }
`;

const ScheduleEventTimes = gql`
  fragment ScheduleEventTimes on ScheduleEvent {
    id
    start
    end
    scheduledStartTime
    scheduledEndTime
    timeZone
  }
`;

const ScheduleEventStatus = gql`
  fragment ScheduleEventStatus on ScheduleEvent {
    id
    status
    timeZone
  }
`;

const ScheduleEventFull = gql`
  fragment ScheduleEventFull on ScheduleEvent {
    id
    summary
    status
    start
    end
    scheduledStartTime
    scheduledEndTime
    timeZone
    assignee {
      id
      name
      image
    }
  }
`;

// Helper: derive a sortable timestamp for jobBoard entries
const getJobBoardEntryEpochMs = (
  entry: ListJobsQuery['jobBoard'][number]
): number => {
  if (!entry) return 0;
  if (entry.__typename === 'Job') {
    const ts = entry.scheduleStart ?? entry.scheduleEnd ?? null;
    return ts ? new Date(ts).getTime() : 0;
  }
  const ts = entry.summary === 'Check-in' ? entry.start : entry.end;
  return ts ? new Date(ts).getTime() : 0;
};

// Always log cache errors to ensure they're visible
const devError = (message: string, error?: Error) => {
  console.error(message, error?.stack || error || '');
  // TODO: Forward to error tracking service if configured
  // if (window.errorTracker) {
  //   window.errorTracker.captureMessage(message, { error });
  // }
};

// Fragment types are imported from generated types
// All ScheduleEvent fragments include timeZone field

// Helper: read timeZone from existing ScheduleEvent in cache
// When SCHEDULE_DATE_RANGE_FILTER is enabled, iterates over all registered
// Schedule query variable combinations since queries with different variables
// are cached separately

const readTimeZoneFromCache = (
  cache: any,
  eventId: string | undefined
): string | null => {
  if (!eventId) return null;
  try {
    if (SCHEDULE_DATE_RANGE_FILTER) {
      // Iterate over all registered variable combinations to find the event
      const variablesList = getRegisteredScheduleVariables();
      for (const variables of variablesList) {
        const data = cache.readQuery({
          query: ScheduleDocument,
          variables,
        }) as ScheduleQuery | null;
        const event = data?.sites
          ?.flatMap((site) => site.calendar)
          .find((e) => e.id === eventId);
        if (event) {
          // timeZone is now queried in Schedule query, so it should be available
          return (event as { timeZone?: string } | undefined)?.timeZone ?? null;
        }
      }
      return null;
    }

    // When flag is disabled, single cache entry without variables
    const data = cache.readQuery({
      query: ScheduleDocument,
    }) as ScheduleQuery | null;
    const event = data?.sites
      ?.flatMap((site) => site.calendar)
      .find((e) => e.id === eventId);
    // timeZone is now queried in Schedule query, so it should be available
    return (event as { timeZone?: string } | undefined)?.timeZone ?? null;
  } catch {
    return null;
  }
};

// Helper: update all cached Schedule queries
// When SCHEDULE_DATE_RANGE_FILTER is enabled, iterates over all registered
// variable combinations. When disabled, updates the single cache entry.

const updateAllScheduleQueries = (
  cache: any,
  updater: (data: ScheduleQuery | null) => ScheduleQuery | null
): void => {
  if (SCHEDULE_DATE_RANGE_FILTER) {
    // Iterate over all registered variable combinations
    const variablesList = getRegisteredScheduleVariables();
    for (const variables of variablesList) {
      cache.updateQuery({ query: ScheduleDocument, variables }, updater);
    }
  } else {
    // Single cache entry without variables
    cache.updateQuery({ query: ScheduleDocument }, updater);
  }
};

type ContactsListArgs = {
  status?: string | string[] | null;
  relationship?: string | string[] | null;
};

function toArgArray(value: string | string[] | null | undefined): string[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

export function createdContactMatchesContactsArgs(
  created: { status?: string | null; relationship?: string[] | null },
  args: ContactsListArgs | null
): boolean {
  if (!args) return true;

  const statuses = toArgArray(args.status);
  if (
    statuses.length > 0 &&
    created.status &&
    !statuses.includes(created.status)
  ) {
    return false;
  }

  const relationships = toArgArray(args.relationship);
  if (relationships.length > 0) {
    const createdRelationships = created.relationship ?? [];
    if (!relationships.some((rel) => createdRelationships.includes(rel))) {
      return false;
    }
  }

  return true;
}

export const graphcacheConfig: GraphCacheConfig = {
  schema,
  keys: {
    AssetSharingDefaults: () => null,
    AssetTaskConfig: () => null,
    Attribute: () => null,
    AttributeSingle: () => null,
    AttributeV2: () => null,
    AutomationRepeat: () => null,
    PlaceTypeConfig: () => null,
    Role: (role) => role.key ?? null,
    SiteTypeCount: () => null,
    Stocktake: () => null,
    VariantType: () => null,
    VariantValue: () => null,
    // IMPORTANT: ScheduleEvent ids collide between jobs and bookings (shared numeric ids).
    // Make cache keys table-aware to avoid merging Booking into Job or vice versa.
    ScheduleEvent: (event) => {
      return getScheduleEventCompositeKey(event);
    },
  },
  resolvers: {
    Query: {
      spacesConnection: relayPagination(),
    },
  },
  optimistic: {
    updatePlaceTypeConfig(args: MutationUpdatePlaceTypeConfigArgs) {
      const input =
        args?.input ?? ({} as MutationUpdatePlaceTypeConfigArgs['input']);
      const isCustomized =
        typeof input.isCustomized === 'boolean'
          ? input.isCustomized
          : Array.isArray(input.navTypes) && input.navTypes.length > 0;
      const result: WithTypename<PlaceTypeConfig> = {
        __typename: 'PlaceTypeConfig',
        types: input.types ?? [],
        orderMode: input.orderMode ?? PlaceTypeOrderMode.ALPHA,
        navTypes: input.navTypes ?? [],
        showAllInNav:
          typeof input.showAllInNav === 'boolean' ? input.showAllInNav : true,
        isCustomized,
        updatedAt: new Date().toISOString(),
      };
      return result;
    },
    updateContactTypeConfig(
      args: import('./generated/graphql').MutationUpdateContactTypeConfigArgs
    ) {
      const input =
        args?.input ??
        ({} as import('./generated/graphql').MutationUpdateContactTypeConfigArgs['input']);
      const isCustomized =
        typeof input.isCustomized === 'boolean'
          ? input.isCustomized
          : Array.isArray(input.navTypes) && input.navTypes.length > 0;
      const result: WithTypename<PlaceTypeConfig> = {
        __typename: 'PlaceTypeConfig',
        types: input.types ?? [],
        orderMode: input.orderMode ?? PlaceTypeOrderMode.ALPHA,
        navTypes: input.navTypes ?? [],
        showAllInNav:
          typeof input.showAllInNav === 'boolean' ? input.showAllInNav : true,
        isCustomized,
        updatedAt: new Date().toISOString(),
      };
      return result;
    },
    renamePlaceType(args: MutationRenamePlaceTypeArgs, cache) {
      const from = args?.input?.from as string | undefined;
      const to = args?.input?.to as string | undefined;
      // Try to base optimistic result on current cache value if possible
      try {
        const data = cache.readQuery({
          query: PlaceTypeConfigDocument,
        }) as { placeTypeConfig?: PlaceTypeConfig } | null;
        const prev = data?.placeTypeConfig;
        if (prev && from && to) {
          const nextTypes = Array.isArray(prev.types)
            ? prev.types.map((t: string) => (t === from ? to : t))
            : [];
          const nextNavTypes = Array.isArray(prev.navTypes)
            ? prev.navTypes.map((t: string) => (t === from ? to : t))
            : [];
          const result: WithTypename<PlaceTypeConfig> = {
            __typename: 'PlaceTypeConfig',
            types: nextTypes,
            orderMode: prev.orderMode ?? PlaceTypeOrderMode.ALPHA,
            navTypes: nextNavTypes,
            showAllInNav:
              typeof prev.showAllInNav === 'boolean' ? prev.showAllInNav : true,
            isCustomized: !!prev.isCustomized,
            updatedAt: new Date().toISOString(),
          };
          return result;
        }
      } catch (err) {
        devError(
          `renamePlaceType: failed to read cache for optimistic update from "${from}" to "${to}"`,
          err as Error
        );
        // fall through to default optimistic value
      }
      const fallback: WithTypename<PlaceTypeConfig> = {
        __typename: 'PlaceTypeConfig',
        types: [],
        orderMode: PlaceTypeOrderMode.ALPHA,
        navTypes: [],
        showAllInNav: true,
        isCustomized: false,
        updatedAt: new Date().toISOString(),
      };
      return fallback;
    },
  },
  updates: {
    Mutation: {
      addItem(result, _args, cache) {
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'items')
          .forEach((field) => {
            cache.updateQuery(
              {
                query: ItemsDocument,
                variables: field.arguments ?? undefined,
              },
              (data: ItemsQuery | null) => {
                if (!data || !Array.isArray(data.items)) return data;
                return {
                  ...data,
                  items: [
                    ...data.items,
                    (result as import('./generated/graphql').AddItemMutation)
                      .addItem,
                  ],
                };
              }
            );
          });
      },

      createAsset(_result, _args, cache) {
        // Response shape may not fully match ListAssets selection; avoid shape mismatch by invalidation
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'assets')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      createContact(result, _args, cache) {
        const created = result.createContact;
        if (!created) return;

        // Append instead of invalidating. Invalidation marks every contacts
        // query stale (including EditJobForm), which remounts the job form.
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'contacts')
          .forEach((field) => {
            if (
              !createdContactMatchesContactsArgs(
                created,
                field.arguments as ContactsListArgs | null
              )
            ) {
              return;
            }

            cache.updateQuery(
              {
                query: ContactsDocument,
                variables: field.arguments ?? undefined,
              },
              (data: ContactsQuery | null) => {
                if (!data || !Array.isArray(data.contacts)) return data;
                if (
                  data.contacts.some((contact) => contact.id === created.id)
                ) {
                  return data;
                }
                return {
                  ...data,
                  contacts: [
                    ...data.contacts,
                    created as ContactsQuery['contacts'][number],
                  ],
                };
              }
            );
          });
      },

      deleteJob(
        result: unknown,
        args: import('./generated/graphql').MutationDeleteJobArgs,
        cache
      ) {
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'jobs')
          .forEach((field) => {
            cache.updateQuery(
              {
                query: JobsDocument,
                variables: field.arguments,
              },
              (data: JobsQuery | null) => {
                if (!data) return data;
                const deleted =
                  typeof (result as { deleteJob?: number })?.deleteJob ===
                  'number'
                    ? (result as { deleteJob?: number }).deleteJob!
                    : (result as { deleteJob?: { output?: number } })?.deleteJob
                        ?.output;
                if (!Array.isArray(data.jobs) || deleted !== 1) return data;

                return {
                  ...data,
                  jobs: data.jobs.filter((job) => job.id !== args.id),
                };
              }
            );
          });

        updateAllScheduleQueries(cache, (data: ScheduleQuery | null) => {
          const sites = data?.sites;
          if (!sites) return data;
          return {
            ...data,
            sites: sites.map((site) => ({
              ...site,
              // Job and booking calendar rows share numeric ids. Only drop
              // the job; leave the colliding booking on the planner.
              calendar: site.calendar.filter(
                (event) => event.id !== args.id || isBookingEvent(event)
              ),
            })),
          } as ScheduleQuery;
        });

        // cache.writeFragment(
        //   gql`
        //     fragment _ on ScheduleEvent {
        //       id
        //       status
        //     }
        //   `,
        //   {
        //     id: _args.id,
        //     status: JobStatus.Deleted,
        //   }
        // );
      },

      createTag(result, _args, cache) {
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'tags')
          .forEach((field) => {
            // cache.updateQuery(
            //   {
            //     query: ListTagsDocument,
            //     variables: { entityTypes: field.arguments.entityTypes },
            //   },
            //   (data) => {
            //     console.log('d', data);
            //     data?.tags.edges.push({ node: result.createTag });
            //     return data;
            //   }
            // );
            cache.invalidate('Query', field.fieldKey);
          });
      },

      rescheduleJob(result, _args, cache) {
        // Update ScheduleEvent start/end times
        const updatedJob = result.rescheduleJob;
        if (!updatedJob || !updatedJob.id) return;
        const eventId = updatedJob.id;
        const existingTimeZone = readTimeZoneFromCache(cache, eventId);
        const timeZone = validateAndNormalizeTimezone(existingTimeZone);

        cache.writeFragment(ScheduleEventStartEnd, {
          id: eventId,
          start: updatedJob.scheduleStart ?? updatedJob.scheduleEnd,
          end: updatedJob.scheduleEnd,
          timeZone,
        } as Partial<ScheduleEventStartEndFragment>);

        // Also persist time fields directly onto the Job entity so Job(id) queries see them
        // This avoids losing times on hard reload if the job detail page is opened.
        try {
          cache.writeFragment(
            gql`
              fragment JobTimes on Job {
                id
                scheduleStart
                scheduleEnd
                scheduledStartDate
                scheduledStartTime
                scheduledEndDate
                scheduledEndTime
                scheduledStartStrict
                scheduledEndStrict
              }
            `,
            jobTimesCacheFields({
              id: eventId,
              scheduleStart: updatedJob.scheduleStart,
              scheduleEnd: updatedJob.scheduleEnd,
              scheduledStartDate: updatedJob.scheduledStartDate,
              scheduledStartTime: updatedJob.scheduledStartTime,
              scheduledEndDate: updatedJob.scheduledEndDate,
              scheduledEndTime: updatedJob.scheduledEndTime,
              scheduledStartStrict: updatedJob.scheduledStartStrict,
              scheduledEndStrict: updatedJob.scheduledEndStrict,
            })
          );
        } catch {
          devError(`Failed to write JobTimes fragment for job ${eventId}`);
        }
      },

      revertJobStatus(result, _args, cache) {
        if (!result.revertJobStatus) return;

        const eventId = result.revertJobStatus.id;
        if (!eventId) return;
        const existingTimeZone = readTimeZoneFromCache(cache, eventId);
        const timeZone = validateAndNormalizeTimezone(existingTimeZone);

        cache.writeFragment(ScheduleEventStatus, {
          id: eventId,
          status: result.revertJobStatus.status,
          timeZone,
        } as Partial<ScheduleEventStatusFragment>);
      },

      completeJob(result, _args, cache) {
        if (!result.completeJob) return;

        const eventId = result.completeJob.id;
        if (!eventId) return;
        const existingTimeZone = readTimeZoneFromCache(cache, eventId);
        const timeZone = validateAndNormalizeTimezone(existingTimeZone);

        cache.writeFragment(ScheduleEventStatus, {
          id: eventId,
          status: result.completeJob.status,
          timeZone,
        } as Partial<ScheduleEventStatusFragment>);
      },

      cancelJob(result, _args, cache) {
        if (!result.cancelJob) return;

        const eventId = result.cancelJob.id;
        if (!eventId) return;
        const existingTimeZone = readTimeZoneFromCache(cache, eventId);
        const timeZone = validateAndNormalizeTimezone(existingTimeZone);

        cache.writeFragment(ScheduleEventStatus, {
          id: eventId,
          status: result.cancelJob.status,
          timeZone,
        } as Partial<ScheduleEventStatusFragment>);
      },

      startJob(result, _args, cache) {
        if (!result.startJob) return;

        const eventId = result.startJob.id;
        if (!eventId) return;
        const existingTimeZone = readTimeZoneFromCache(cache, eventId);
        const timeZone = validateAndNormalizeTimezone(existingTimeZone);

        cache.writeFragment(ScheduleEventStatus, {
          id: eventId,
          status: result.startJob.status,
          timeZone,
        } as Partial<ScheduleEventStatusFragment>);
      },

      updateJob(
        result: unknown,
        args: import('./generated/graphql').MutationUpdateJobArgs,
        cache
      ) {
        const updatedJob = (result as import('./gql/graphql').UpdateJobMutation)
          ?.updateJob;
        if (!updatedJob) return;

        // Updates cache of jobBoard
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'jobBoard')
          .forEach((field) => {
            const { scheduleFrom, scheduleTo, ...rest } =
              field.arguments as QueryJobBoardArgs;
            cache.updateQuery(
              {
                query: ListJobsDocument,
                variables: {
                  ...rest,
                  dateAfter: scheduleFrom,
                  dateBefore: scheduleTo,
                },
              },
              (data: ListJobsQuery | null) => {
                if (!data || !Array.isArray(data.jobBoard)) return data;
                return {
                  ...data,
                  jobBoard: [...data.jobBoard].sort(
                    (a, b) =>
                      getJobBoardEntryEpochMs(a) - getJobBoardEntryEpochMs(b)
                  ),
                };
              }
            );
          });

        // Prepare partial fragment write to avoid clobbering cached fields
        const eventId = updatedJob.id;
        const existingTimeZone = readTimeZoneFromCache(cache, eventId);
        const timeZone = validateAndNormalizeTimezone(existingTimeZone);

        const fragmentData: Record<string, unknown> = {
          id: eventId,
          summary: updatedJob.name,
          status: updatedJob.status,
          start: updatedJob.scheduleStart ?? updatedJob.scheduleEnd,
          end: updatedJob.scheduleEnd,
          timeZone,
        };

        // Only include scheduled times if provided; otherwise leave existing cache values intact
        if (typeof args.input.scheduledStartTime !== 'undefined') {
          fragmentData.scheduledStartTime = args.input.scheduledStartTime;
        }
        if (typeof args.input.scheduledEndTime !== 'undefined') {
          fragmentData.scheduledEndTime = args.input.scheduledEndTime;
        }

        // Include assignee with image field to satisfy ScheduleEventFull fragment requirements
        // The image field must be explicitly set (even to null) to avoid graphcache undefined errors
        if (updatedJob.assignee) {
          fragmentData.assignee = {
            __typename: 'Contact',
            id: updatedJob.assignee.id,
            name: updatedJob.assignee.name,
            // Use nullish coalescing to ensure image is null (not undefined) if not present
            image:
              (updatedJob.assignee as { image?: string | null }).image ?? null,
          };
        } else {
          fragmentData.assignee = null;
        }

        cache.writeFragment(
          ScheduleEventFull,
          fragmentData as Partial<ScheduleEventFullFragment>
        );

        // Handle changed site
        // Use the response location (more reliable than input shape)
        type UpdateJobMaybeLoc = {
          updateJob?: { location?: { id?: string } | null };
        };
        const updatedJobWithLoc = (result as UpdateJobMaybeLoc).updateJob;
        if (updatedJobWithLoc?.location?.id) {
          // Find the old event in the cache to reinstate it later
          // When SCHEDULE_DATE_RANGE_FILTER is enabled, search all cached queries
          let oldEvent:
            | ScheduleQuery['sites'][number]['calendar'][number]
            | undefined;

          if (SCHEDULE_DATE_RANGE_FILTER) {
            const variablesList = getRegisteredScheduleVariables();
            for (const variables of variablesList) {
              const read = cache.readQuery({
                query: ScheduleDocument,
                variables,
              }) as ScheduleQuery | null | undefined;
              oldEvent = read?.sites
                ?.flatMap((site) => site.calendar)
                .find(
                  (event) =>
                    event.id === args.input.id && !isBookingEvent(event)
                );
              if (oldEvent) break;
            }
          } else {
            const read = cache.readQuery({ query: ScheduleDocument }) as
              | ScheduleQuery
              | null
              | undefined;
            oldEvent = read?.sites
              ?.flatMap((site) => site.calendar)
              .find(
                (event) => event.id === args.input.id && !isBookingEvent(event)
              );
          }

          if (!oldEvent) {
            devError(
              `Could not find event in cache to update site. event.id: ${args.input.id} event.name: ${args.input.name}`
            );
            return;
          }

          // Single-pass: remove from all, add to new
          const newSiteId = updatedJobWithLoc.location!.id;
          updateAllScheduleQueries(cache, (data: ScheduleQuery | null) => {
            const sites = data?.sites;
            if (!sites) return data;
            return {
              ...data,
              sites: sites.map((s) => {
                const without = s.calendar.filter(
                  (e) => e.id !== args.input.id || isBookingEvent(e)
                );
                if (s.id === newSiteId && oldEvent) {
                  return { ...s, calendar: [...without, oldEvent] };
                }
                return { ...s, calendar: without };
              }),
            } as ScheduleQuery;
          });
        }
      },

      rescheduleJobs(
        result: unknown,
        _args: import('./gql/graphql').RescheduleJobsMutationVariables,
        cache
      ) {
        // Ensure calendar events reflect new scheduled times immediately
        const updated = (
          result as {
            rescheduleJobs?: Array<{
              id?: string;
              scheduleStart?: string | null;
              scheduleEnd?: string | null;
              scheduledStartDate?: string | null;
              scheduledStartTime?: string | null;
              scheduledEndDate?: string | null;
              scheduledEndTime?: string | null;
              scheduledStartStrict?: boolean;
              scheduledEndStrict?: boolean;
            }>;
          }
        ).rescheduleJobs;
        if (!updated || updated.length === 0) return;

        updated.forEach((u) => {
          if (!u.id) return;
          const times = jobTimesCacheFields({
            id: u.id,
            scheduleStart: u.scheduleStart,
            scheduleEnd: u.scheduleEnd,
            scheduledStartDate: u.scheduledStartDate,
            scheduledStartTime: u.scheduledStartTime,
            scheduledEndDate: u.scheduledEndDate,
            scheduledEndTime: u.scheduledEndTime,
            scheduledStartStrict: u.scheduledStartStrict,
            scheduledEndStrict: u.scheduledEndStrict,
          });

          // Read timeZone from cache for this event
          const existingTimeZone = readTimeZoneFromCache(cache, u.id);
          const timeZone = validateAndNormalizeTimezone(existingTimeZone);

          // Write both fragments atomically to ensure cache consistency.
          // If either write fails, both should fail to prevent inconsistent state.
          try {
            cache.writeFragment(ScheduleEventTimes, {
              id: u.id,
              start: u.scheduleStart ?? u.scheduleEnd ?? null,
              end: u.scheduleEnd ?? null,
              scheduledStartTime: times.scheduledStartTime,
              scheduledEndTime: times.scheduledEndTime,
              timeZone,
            } as Partial<ScheduleEventTimesFragment>);

            // Also persist time fields directly onto the Job entity so Job(id) queries see them
            // This avoids losing times on hard reload if the job detail page is opened.
            cache.writeFragment(
              gql`
                fragment JobTimes on Job {
                  id
                  scheduleStart
                  scheduleEnd
                  scheduledStartDate
                  scheduledStartTime
                  scheduledEndDate
                  scheduledEndTime
                  scheduledStartStrict
                  scheduledEndStrict
                }
              `,
              times
            );
          } catch (err) {
            devError(
              `rescheduleJobs: failed to write JobTimes fragment for job ${u.id}`,
              err as Error
            );
          }
        });
      },

      createJobs(
        result: unknown,
        _args: import('./gql/graphql').CreateJobsMutationVariables,
        cache
      ) {
        const created = (
          result as {
            createJobs?: Array<{
              id?: string;
              name?: string;
              status?: JobStatus;
              scheduledStartDate?: string | null;
              scheduledEndDate?: string | null;
              location?: { id?: string } | null;
            }>;
          }
        ).createJobs;
        if (!created) return;

        // Invalidate asset jobs cache for any assets that have jobs created
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'asset')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });

        updateAllScheduleQueries(cache, (data: ScheduleQuery | null) => {
          const sites = data?.sites;
          if (!sites || !created) return data;
          const additionsBySite = new Map<
            string,
            Array<ScheduleQuery['sites'][number]['calendar'][number]>
          >();
          for (const event of created) {
            if (!event?.id || !event.location?.id) continue;
            const locationId = event.location.id;
            const list = additionsBySite.get(locationId) ?? [];
            // For new events, try to get timeZone from the site's calendar events
            // or use browser timezone as fallback
            const site = sites.find((s) => s.id === locationId);
            // timeZone is now queried in Schedule query, so it should be available
            // Type assertion needed until codegen regenerates types with timeZone field
            const siteTimeZone =
              (site?.calendar?.[0] as { timeZone?: string } | undefined)
                ?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
            const timeZone = validateAndNormalizeTimezone(siteTimeZone);

            list.push({
              __typename: 'ScheduleEvent',
              id: event.id,
              summary: event.name ?? '',
              start: event.scheduledStartDate ?? event.scheduledEndDate ?? null,
              // We do not get scheduledStartTime from this mutation selection.
              // Keep null to avoid inventing times; card renderers now respect nulls.
              scheduledStartTime: null,
              end: null,
              scheduledEndTime: null,
              status: event.status ?? JobStatus.Created,
              assignee: null,
              included: null,
              // GraphQL expects arrays (even if empty) for list types, not null
              attributes: [],
              tags: [],
              timeZone,
            } as ScheduleQuery['sites'][number]['calendar'][number]);
            additionsBySite.set(locationId, list);
          }
          if (additionsBySite.size === 0) return data;
          return {
            ...data,
            sites: sites.map((s) => {
              const add = additionsBySite.get(s.id);
              if (!add || add.length === 0) return s;
              return { ...s, calendar: [...s.calendar, ...add] };
            }),
          } as ScheduleQuery;
        });
      },

      completeAttributeTask(result, args, cache) {
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'task')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
      },

      createDeployment(result, args, cache) {
        // When a deployment is created, invalidate task queries since tasks
        // contain asset deployment information that needs to be refreshed
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'task')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });

        // Also invalidate asset queries to ensure asset deployment data is fresh
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'asset')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
      },

      addTagsToTaggable(result, args, cache) {
        // When tags are added to a Task, invalidate task queries, automations queries,
        // and jobTemplates queries to refresh tag data
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'task')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'automations')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'jobTemplates')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
      },

      removeTagsFromTaggable(result, args, cache) {
        // When tags are removed from a Task, invalidate task queries, automations queries,
        // and jobTemplates queries to refresh tag data
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'task')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'automations')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
        cache
          .inspectFields('Query')
          .filter((field) => field.fieldName === 'jobTemplates')
          .forEach((field) => {
            cache.invalidate('Query', field.fieldKey);
          });
      },

      updateAttributeValues(
        result: unknown,
        args: import('./generated/graphql').MutationUpdateAttributeValuesArgs,
        cache
      ) {
        const updated = (result as UpdateAttributeValuesMutation)
          .updateAttributeValues;
        if (!updated || !args.input.id) return;

        // Extract entity type and ID from the input (e.g., "Asset:123" -> "Asset", "123")
        const [entityType, entityId] = args.input.id.split(':');

        if (entityType === 'Asset') {
          cache
            .inspectFields('Query')
            .filter(
              (field) =>
                field.fieldName === 'asset' && field.arguments?.id === entityId
            )
            .forEach((field) => {
              cache.invalidate('Query', field.fieldKey);
            });
        }

        // Handle other entity types (Site, Contact, Item) if needed
        // For now, just invalidate their queries to trigger refetch
        if (entityType !== 'Asset') {
          cache
            .inspectFields('Query')
            .filter((field) => {
              // Invalidate queries that might contain this entity's attributes
              return (
                (field.fieldName === 'site' &&
                  field.arguments?.id === entityId) ||
                (field.fieldName === 'contact' &&
                  field.arguments?.id === entityId) ||
                (field.fieldName === 'item' && field.arguments?.id === entityId)
              );
            })
            .forEach((field) => {
              cache.invalidate('Query', field.fieldKey);
            });
        }
      },

      // When a new site is created, invalidate type counts so nav updates
      createSite(_result: unknown, _args: unknown, cache) {
        // Invalidate counts used by the nav
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'siteTypeCounts')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
        // Also invalidate list queries so any derived "types in use" refreshes
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'sites')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      // When a site's type changes, invalidate type counts so nav updates
      updateSite(_result: unknown, _args: unknown, cache) {
        // Invalidate counts used by the nav
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'siteTypeCounts')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));

        cache
          .inspectFields('Query')
          .filter((f) => {
            // Only invalidate queries with type/status filters
            const args = f.arguments as
              | { type?: unknown; status?: unknown }
              | null
              | undefined;
            return (
              f.fieldName === 'sites' &&
              (args?.type !== undefined || args?.status !== undefined)
            );
          })
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      updateAsset(_result: unknown, _args: unknown, cache) {
        // Don't blanket-invalidate to avoid cache thrashing during bulk updates.
        // urql's normalized cache will automatically update the Asset entity.
        // Only invalidate queries with filters that might exclude the updated asset.
        cache
          .inspectFields('Query')
          .filter((f) => {
            // Only invalidate queries with status filters (e.g., active-only lists)
            const args = f.arguments as { status?: unknown } | null | undefined;
            return f.fieldName === 'assets' && args?.status !== undefined;
          })
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      updatePlaceTypeConfig(result, _args, cache) {
        // Write the returned config directly into the Query.placeTypeConfig cache entry
        cache.updateQuery(
          {
            query: PlaceTypeConfigDocument,
          },
          (data: { placeTypeConfig?: PlaceTypeConfig } | null) => {
            const updated = (
              result as { updatePlaceTypeConfig?: PlaceTypeConfig }
            ).updatePlaceTypeConfig;
            if (!updated) return data;
            return {
              ...data,
              placeTypeConfig: updated,
            };
          }
        );
      },
      updateContactTypeConfig(result, _args, cache) {
        // Write the returned config directly into the Query.contactTypeConfig cache entry
        cache.updateQuery(
          {
            query: ContactTypeConfigDocument,
          },
          (data: { contactTypeConfig?: PlaceTypeConfig } | null) => {
            const updated = (
              result as { updateContactTypeConfig?: PlaceTypeConfig }
            ).updateContactTypeConfig;
            if (!updated) return data;
            return {
              ...data,
              contactTypeConfig: updated,
            };
          }
        );
      },

      renamePlaceType(
        result: unknown,
        _args: import('./generated/graphql').MutationRenamePlaceTypeArgs,
        cache
      ) {
        // Update placeTypeConfig and invalidate counts
        cache.updateQuery(
          {
            query: PlaceTypeConfigDocument,
          },
          (data: { placeTypeConfig?: PlaceTypeConfig } | null) => {
            const renamed = (result as { renamePlaceType?: PlaceTypeConfig })
              .renamePlaceType;
            if (!renamed) return data;
            return {
              ...data,
              placeTypeConfig: renamed,
            };
          }
        );
        // Invalidate siteTypeCounts queries to force fresh counts
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'siteTypeCounts')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
        // Invalidate all sites queries so type labels refresh immediately
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'sites')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      createAutomation(_result: unknown, _args: unknown, cache) {
        // When an automation is created, invalidate automations queries
        // to ensure the list refreshes with the new automation
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'automations')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      updateAutomation(_result: unknown, _args: unknown, cache) {
        // When an automation is updated, invalidate automations queries
        // to ensure the view refreshes with updated template data (including includeAttributes)
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'automations')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      updateJobTemplate(_result: unknown, _args: unknown, cache) {
        // When a job template is updated, invalidate related queries
        // to ensure automations and templates refresh with updated data (including includeAttributes)
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'jobTemplates')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'automations')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },

      createJobTemplate(_result: unknown, _args: unknown, cache) {
        // When a job template is created, invalidate jobTemplates queries
        // to ensure the list refreshes with the new template
        cache
          .inspectFields('Query')
          .filter((f) => f.fieldName === 'jobTemplates')
          .forEach((f) => cache.invalidate('Query', f.fieldKey));
      },
    },
  },
};

export const cache = cacheExchange(graphcacheConfig);
